import {toInt} from './toInt';

// return true on error
export type ValidatorFn<TField> = (value: TField) => boolean;

function required(value: string | number | null | any): boolean {
  if (value === null || value === undefined)
    return true;
  if (value === '')
    return true;
  return false;
}

function patternMatcher(pattern: string | RegExp): ValidatorFn<string> {
  let regExp: RegExp;
  if (typeof pattern === 'string')
    regExp = new RegExp(pattern);
  else
    regExp = pattern;

  return (value) => {
    return !regExp.test(value);
  };
}

function intString(minValue: number = 0, maxValue: number = 2147483647): ValidatorFn<string> {
  return (value) => {
    if (value === '')
      return false;
    return toInt(value, minValue, maxValue) === null;
  };
}

/******************************************************************************/

const strictDatePattern = new RegExp('^(\\d{4})-(\\d{2})-(\\d{2})$');

function strictDateValidatorFn(value: string): boolean {
  if (value === '')
    return false;

  let matches = strictDatePattern.exec(value);
  if (matches == null)
    return true;
  let year = parseInt(matches[1], 10);
  let month = parseInt(matches[2], 10);
  let day = parseInt(matches[3], 10);
  if (month === 0 || month > 12)
    return true;

  let date = new Date(year, month - 1, day);
  if (date.getFullYear() !== year ||
    date.getMonth() !== (month - 1) ||
    date.getDate() !== day)
    return true;
  return false;
}

/******************************************************************************/
const timePattern = new RegExp('^(\\d{2}):(\\d{2})$');

function timeValidatorFn(value: string): boolean {
  if (value === '')
    return false;

  if (!timePattern.test(value))
    return true;
  let hour = parseInt(value.substring(0, 2), 10);
  let minutes = parseInt(value.substring(3, 5), 10);
  if (hour > 23)
    return true;
  if (minutes > 59)
    return true;
  return false;
}

/******************************************************************************/

const partialDatePattern = new RegExp('^\\d{4}(-\\d{2}(-\\d{2})?)?$');

function partialDateValidatorFn(value: string): boolean {
  if (value === '')
    return false;

  if (!partialDatePattern.test(value))
    return true;

  let year = 0;
  let month = 0;
  let day = 0;

  if (value.length >= 4)
    year = parseInt(value.substr(0, 4), 10);
  if (value.length >= 7)
    month = parseInt(value.substr(5, 2), 10);
  if (value.length === 10)
    day = parseInt(value.substr(8, 2), 10);
  if (value.length >= 7 && (month === 0 || month > 12))
    return true;
  if (day === 0)
    return false;
  let date = new Date(year, month - 1, day);
  if (date.getFullYear() !== year ||
    date.getMonth() !== (month - 1) ||
    date.getDate() !== day)
    return true;
  return false;
}

/******************************************************************************/

const compactPartialDatePattern = new RegExp('^\\d{4}(\\d{2}(\\d{2})?)?$');

function compactPartialDateValidatorFn(value: string): boolean {
  if (value === '')
    return false;

  if (!compactPartialDatePattern.test(value))
    return true;

  let year = 0;
  let month = 0;
  let day = 0;

  if (value.length >= 4)
    year = parseInt(value.substr(0, 4), 10);
  if (value.length >= 6)
    month = parseInt(value.substr(4, 2), 10);
  if (value.length === 8)
    day = parseInt(value.substr(6, 2), 10);
  if (value.length >= 6 && (month === 0 || month > 12))
    return true;
  if (day === 0)
    return false;
  let date = new Date(year, month - 1, day);
  if (date.getFullYear() !== year ||
    date.getMonth() !== (month - 1) ||
    date.getDate() !== day)
    return true;
  return false;
}

function dateRangeValidatorFn(value: string): boolean {
  if (value === '')
    return false;

  if (value.indexOf('-') === -1)
    return compactPartialDateValidatorFn(value);

  let parts = value.split('-');
  if (parts.length > 2)
    return true;

  let part1 = parts[0];
  let part2 = parts[1];

  if (part1 === '' && part2 === '')
    return true;

  if (part1 === '')
    return compactPartialDateValidatorFn(part2);
  if (part2 === '')
    return compactPartialDateValidatorFn(part1);

  return compactPartialDateValidatorFn(part1) || compactPartialDateValidatorFn(part2);
}

function integerValidatorFn(value: string): boolean {
  if (value === '')
    return false;
    return Number.isInteger(value);
}
/******************************************************************************/

export class Validators {
  readonly required = required;
  readonly pattern = patternMatcher;
  readonly intString = intString;
  readonly strictDate = strictDateValidatorFn;
  readonly partialDate = partialDateValidatorFn;
  readonly compactDate = compactPartialDateValidatorFn;
  readonly dateRange = dateRangeValidatorFn;
  readonly time = timeValidatorFn;
  readonly integer = integerValidatorFn;
}

export const validators = new Validators();
